import React from 'react';
import { theme } from '../../theme';
import styled from '@emotion/styled';

type SubtleButtonProps = {
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  size?: 'small' | 'medium';
  color?: 'default' | 'highlighted' | 'danger';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  'data-cy'?: string;
  'data-icom'?: string;
  iconColor?: string;
  iconScale?: number | undefined;
  type?: 'button' | 'submit';
};

export const SubtleButton = ({
  children,
  onClick,
  size = 'medium',
  color = 'default',
  leftIcon,
  rightIcon,
  disabled = false,
  className,
  style,
  'data-cy': dataCy,
  'data-icom': dataIcom,
  iconColor,
  iconScale,
  type = 'button',
}: SubtleButtonProps) => {
  const justIcon = (leftIcon && !children && !rightIcon) || (rightIcon && !children && !leftIcon);
  return (
    <SubtleButtonComponent
      size={size}
      color={color}
      disabled={disabled}
      className={className}
      style={style}
      data-cy={dataCy}
      data-icom={dataIcom}
      onClick={onClick}
      justIcon={!!justIcon}
      type={type}
    >
      {leftIcon && (
        <IconWrapper color={color} disabled={disabled} size={size} iconColor={iconColor} iconScale={iconScale}>
          {leftIcon}
        </IconWrapper>
      )}
      {children && <span>{children}</span>}
      {rightIcon && (
        <IconWrapper color={color} disabled={disabled} size={size} iconColor={iconColor} iconScale={iconScale}>
          {rightIcon}
        </IconWrapper>
      )}
    </SubtleButtonComponent>
  );
};

const IconWrapper = styled.div<{
  color: string;
  disabled: boolean;
  size: string;
  iconColor?: string;
  iconScale?: number;
}>`
  width: ${({ size }) => (size === 'small' ? '16px' : '22px')};
  height: ${({ size }) => (size === 'small' ? '16px' : '22px')};
  align-self: start;
  transform: ${({ iconScale }) => (iconScale ? `scale(${iconScale})` : 'scale(1)')};

  &,
  & svg,
  & path {
    fill: ${(props: { color: string; disabled: boolean; iconColor?: string }) => {
      const { color, disabled, iconColor } = props;
      if (disabled) {
        return theme.colors.iconText.disabled;
      } else {
        if (iconColor) {
          return iconColor;
        } else {
          if (color === 'highlighted') {
            return theme.colors.actionPrimary.default;
          } else if (color === 'danger') {
            return theme.colors.emotionDanger.default;
          } else {
            return theme.colors.iconText.primary;
          }
        }
      }
    }};
  }
  > svg {
    width: 100%;
    height: 100%;
  }
`;

type SubtleButtonComponentProps = Pick<SubtleButtonProps, 'size' | 'color' | 'disabled'> & { justIcon?: boolean };

const SubtleButtonComponent = styled.button<SubtleButtonComponentProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  letter-spacing: ${({ size }) => (size === 'small' ? '0.6px' : '0.75px')};
  gap: ${({ size }) => {
    if (size === 'small') {
      return theme.spacing.d;
    } else {
      return theme.spacing.e;
    }
  }};
  cursor: ${({ disabled }) => {
    if (disabled) {
      return 'not-allowed';
    } else {
      return 'pointer';
    }
  }};
  background-color: ${({ disabled }) => {
    if (disabled) {
      return theme.colors.backgroundCover.disabled;
    } else {
      return theme.colors.backgroundCover.default;
    }
  }};
  border: 1px solid
    ${({ disabled }) => {
      if (disabled) {
        return theme.colors.borderMain.disabled;
      } else {
        return theme.colors.borderMain.default;
      }
    }};
  border-radius: ${theme.borderRadius.l};
  color: ${({ disabled, color }) => {
    if (disabled) {
      return theme.colors.text.disabled;
    } else {
      if (color === 'danger') {
        return theme.colors.emotionDanger.default;
      } else {
        return theme.colors.text.primary;
      }
    }
  }};
  font-size: ${({ size }) => {
    if (size === 'small') {
      return theme.typography.body.small.button.fontSize;
    } else {
      return theme.typography.body.medium.button.fontSize;
    }
  }};
  font-weight: ${({ size }) => {
    if (size === 'small') {
      return theme.typography.body.small.button.fontWeight;
    } else {
      return theme.typography.body.medium.button.fontWeight;
    }
  }};
  padding: ${({ justIcon, size }) =>
    justIcon
      ? size === 'small'
        ? '7.5px'
        : '7px' // Mickey said the width and height must be ABSOLUTELY the same as in figma that does not take border into account
      : size === 'medium'
        ? `7px 15px`
        : `7px 11px`};
  transition: all 0.2s linear;

  &:hover {
    background: ${({ disabled }) => {
      if (disabled) {
        return theme.colors.backgroundCover.disabled;
      } else {
        return theme.colors.backgroundCover.hover;
      }
    }};
    border-color: ${({ disabled }) => {
      if (disabled) {
        return theme.colors.borderMain.disabled;
      } else {
        return theme.colors.borderMain.default;
      }
    }};
  }
`;
